/**
* Police Clearance Certificate (PCC / Police Check / ใบตรวจประวัติอาชญากรรม)
* — single source of truth for the /pcc/* cluster.
*
* Provider workflow: liaison, appointment booking, fingerprinting escort,
* document runner to the Criminal Records Division (กองทะเบียนประวัติ
* อาชญากร, สำนักงานตำรวจแห่งชาติ, ปทุมวัน), certified translation, MFA
* legalization and embassy chain — end-to-end for Thai nationals and
* foreigners in Thailand or overseas via power of attorney.
*
* Powers 14 service variants × 22 locations × 10 destinations from the
* 300-keyword universe below.
*/

// ── Keyword universe (300 keywords, 8 groups) ────────────────────────────
export const PCC_KEYWORD_GROUPS = [
  {
    id: "core",
    label: "คำหลัก / Core services",
    keywords: [
      "ตรวจประวัติอาชญากรรม", "ใบตรวจประวัติอาชญากรรม", "ขอประวัติอาชญากรรม",
      "บริการขอใบตรวจประวัติอาชญากรรม", "รับขอใบตรวจประวัติอาชญากรรม",
      "ตัวแทนขอใบตรวจประวัติอาชญากรรม", "เอเจนซี่ขอประวัติอาชญากรรม",
      "จ้างขอประวัติอาชญากรรม", "รับทำใบตรวจประวัติอาชญากรรม",
      "police clearance certificate thailand", "thailand police check",
      "criminal record check thailand", "thai police clearance",
      "police clearance certificate bangkok", "royal thai police check",
      "police certificate thailand", "บริการขอ police clearance certificate",
      "รับทำ police check thailand", "ตรวจสอบประวัติอาชญากรรม",
      "ขอหนังสือรับรองความประพฤติ", "หนังสือรับรองความประพฤติ",
      "บริการขอหนังสือรับรองความประพฤติ", "ใบรับรองประวัติอาชญากรรม",
      "ขอใบตำรวจ ออสเตรเลีย", "ขอใบตำรวจ อเมริกา", "ขอใบตำรวจ แคนาดา",
      "thai police clearance service", "bangkok police clearance agency",
      "background check thailand", "criminal background check thailand",
      "ขอประวัติพิมพ์ลายนิ้วมือ", "ตรวจสอบประวัติบุคคล", "ค้นประวัติอาชญากรรม",
      "บริการเช็คประวัติอาชญากรรม", "ตรวจประวัติอาชญากรรม กองทะเบียนประวัติอาชญากร",
      "ศูนย์บริการออกหนังสือรับรองความประพฤติ", "police check agency thailand",
      "police clearance service bangkok", "background check company thailand",
      "รับเดินเรื่องขอประวัติอาชญากรรม",
    ],
  },
  {
    id: "foreigner",
    label: "สำหรับชาวต่างชาติ / Foreigners & Expats",
    keywords: [
      "police clearance for foreigners thailand",
      "how to get police clearance in thailand for foreigners",
      "thai police clearance certificate for expats",
      "criminal record check for foreigners thailand",
      "non-immigrant visa police check thailand", "thai visa background check",
      "teaching in thailand police check", "police check for digital nomads thailand",
      "royal thai police clearance for foreigners",
      "apply thai police certificate from overseas",
      "get thai police check from abroad", "non-citizen police clearance thailand",
      "alien criminal record check thailand", "foreign worker background check thailand",
      "police check for work permit thailand", "thai background check english",
      "foreign teacher police clearance thailand",
      "how to get royal thai police clearance certificate",
      "thai police certificate requirements for foreigners",
      "police clearance certificate bangkok for non-thais",
      "finger print for foreign country visa in thailand",
      "criminal background screening for expats bangkok",
      "smart visa thailand police check", "ltr visa thailand police check",
      "elite visa thailand criminal record check",
    ],
  },
  {
    id: "use-case",
    label: "วัตถุประสงค์ / Use cases & Visas",
    keywords: [
      "ตรวจประวัติอาชญากรรม ยื่นวีซ่า", "ขอใบตรวจประวัติอาชญากรรม ยื่น pr",
      "ใบตรวจประวัติอาชญากรรม วีซ่าอเมริกา", "ใบตรวจประวัติอาชญากรรม วีซ่าออสเตรเลีย",
      "ใบตรวจประวัติอาชญากรรม วีซ่าอังกฤษ", "ใบตรวจประวัติอาชญากรรม วีซ่าแคนาดา",
      "ใบตรวจประวัติอาชญากรรม วีซ่าเชงเก้น", "ตรวจประวัติอาชญากรรม ทำงานต่างประเทศ",
      "ตรวจประวัติอาชญากรรม สมัครงาน", "ขอหนังสือรับรองความประพฤติ ย้ายประเทศ",
      "police clearance for us visa thailand", "police check for australian visa thailand",
      "police certificate for canada visa bangkok", "criminal record check for uk visa thailand",
      "police check for green card application", "police clearance for immigration purposes",
      "ตรวจประวัติอาชญากรรม วีซ่าคู่รัก", "ตรวจประวัติอาชญากรรม วีซ่าแต่งงาน",
      "ตรวจประวัติอาชญากรรม วีซ่านักเรียน", "police check for student visa",
      "police check for k1 visa thailand", "police clearance for spousal visa",
      "ขอประวัติอาชญากรรม ไปทำงานเรือสำราญ", "ตรวจประวัติอาชญากรรม เรียนต่อต่างประเทศ",
      "background check for employment visa", "police verification for overseas job",
      "ขอใบความประพฤติ ไปอเมริกา", "ขอใบความประพฤติ ไปออส",
      "ใบตรวจประวัติอาชญากรรม ข้าราชการ", "police clearance for working holiday visa",
    ],
  },
  {
    id: "speed-price",
    label: "รูปแบบ ความเร็ว ราคา / Speed & Price",
    keywords: [
      "ตรวจประวัติอาชญากรรม ด่วน", "ขอใบตรวจประวัติอาชญากรรม เร่งด่วน",
      "รับทำ police clearance ด่วน", "ตรวจประวัติอาชญากรรม กี่วันได้",
      "ขอประวัติอาชญากรรม ด่วนที่สุด", "urgent police clearance thailand",
      "fast track police check bangkok", "express police certificate thailand",
      "ตรวจประวัติอาชญากรรม ราคา", "ค่าบริการขอใบตรวจประวัติอาชญากรรม",
      "จ้างขอประวัติอาชญากรรม ราคา", "police clearance certificate thailand cost",
      "thai police check fee", "how much is police check in thailand",
      "ตรวจประวัติอาชญากรรม ราคาถูก", "ขอประวัติอาชญากรรม 3 วันได้",
      "ขอประวัติอาชญากรรม 1 วันได้", "same day police check bangkok",
      "บริการขอประวัติอาชญากรรม ส่งไปรษณีย์", "ขอประวัติอาชญากรรม ออนไลน์ ส่งถึงบ้าน",
      "police check mail delivery thailand", "หักค่าบริการ ขอใบความประพฤติ",
      "เรทราคา เอเจนซี่ ขอใบตำรวจ", "ตรวจประวัติอาชญากรรม นอกสถานที่",
      "mobile fingerprinting service bangkok",
    ],
  },
  {
    id: "bundle",
    label: "บริการเสริม แปล+รับรอง / Bundles",
    keywords: [
      "แปลใบตรวจประวัติอาชญากรรม", "แปลใบตรวจประวัติอาชญากรรม naati",
      "แปลหนังสือรับรองความประพฤติ อังกฤษ", "แปลประวัติอาชญากรรม รับรองกงสุล",
      "รับรอง mfa ใบตรวจประวัติอาชญากรรม", "notarize police clearance thailand",
      "translate thai police certificate to english",
      "police certificate legalization thailand",
      "legalization police clearance mfa bangkok", "แปลใบตำรวจ ยื่นวีซ่า",
      "แปลประวัติอาชญากรรม โดยนักแปลที่รับรอง",
      "notarized criminal record thailand", "apostille police clearance thailand",
      "รับรองสถานทูต ใบตรวจประวัติอาชญากรรม", "embassy legalization police check thailand",
      "แปลและรับรองกงสุล ใบรับรองความประพฤติ", "certified translation thai police clearance",
      "แปลไทยเป็นอังกฤษ ใบประวัติอาชญากรรม",
      "รับรองเอกสารกระทรวงต่างประเทศ ใบประวัติอาชญากรรม",
      "แปลและรับรอง notary public ใบตรวจประวัติอาชญากรรม",
    ],
  },
  {
    id: "location",
    label: "พื้นที่ / Location-based",
    keywords: [
      "ขอประวัติอาชญากรรม กรุงเทพ", "ขอประวัติอาชญากรรม เชียงใหม่",
      "ขอประวัติอาชญากรรม ภูเก็ต", "ขอประวัติอาชญากรรม พัทยา",
      "ขอประวัติอาชญากรรม ชลบุรี", "ขอประวัติอาชญากรรม อุดรธานี",
      "ขอประวัติอาชญากรรม ขอนแก่น", "ขอประวัติอาชญากรรม ใกล้ฉัน",
      "ศูนย์ขอประวัติอาชญากรรม ใกล้ bts", "police clearance office bangkok",
      "police clearance certificate chiang mai", "police clearance phuket",
      "police check pattaya", "กองทะเบียนประวัติอาชญากร ปทุมวัน",
      "อาคาร 7 สำนักงานตำรวจแห่งชาติ ขอประวัติ",
      "ศูนย์บริการออกหนังสือรับรองความประพฤติ สตช",
      "police clearance pathum wan", "criminal record check branch office thailand",
      "ร้านรับทำประวัติอาชญากรรม กรุงเทพ", "เอเจนซี่ขอใบตำรวจ เชียงใหม่",
      "หานายหน้าขอประวัติอาชญากรรม ภูเก็ต", "บริการขอใบความประพฤติ นนทบุรี",
      "บริการขอใบความประพฤติ สมุทรปราการ",
      "ตรวจประวัติอาชญากรรม สำนักงานตำรวจแห่งชาติ",
      "royal thai police headquarters police clearance",
    ],
  },
  {
    id: "process",
    label: "ขั้นตอน วิธีการ / Process",
    keywords: [
      "วิธีขอใบตรวจประวัติอาชญากรรม", "ขั้นตอน ขอใบตรวจประวัติอาชญากรรม",
      "เอกสารที่ต้องใช้ ขอประวัติอาชญากรรม", "ขอประวัติอาชญากรรม ออนไลน์",
      "ลงทะเบียนตรวจประวัติอาชญากรรม ออนไลน์",
      "พิมพ์ลายนิ้วมือ ขอประวัติอาชญากรรม ทำอย่างไร",
      "แบบฟอร์ม ขอหนังสือรับรองความประพฤติ",
      "how to apply for police clearance certificate in thailand",
      "documents required for thai police clearance",
      "thai police clearance application form pdf",
      "online application for thai police clearance",
      "fingerprint check for criminal record thailand",
      "ขอใบตรวจประวัติอาชญากรรม ใช้เวลากี่วัน",
      "เช็คสถานะ ใบตรวจประวัติอาชญากรรม", "ตรวจประวัติอาชญากรรมด้วยตนเอง",
      "หนังสือมอบอำนาจ ขอประวัติอาชญากรรม", "power of attorney for thai police check",
      "จองคิว ขอหนังสือรับรองความประพฤติ", "นัดหมาย ตรวจประวัติอาชญากรรม",
      "พิมพ์ลายนิ้วมือ สถานีตำรวจ ใกล้บ้าน",
      "local police station fingerprinting thailand",
      "รูปถ่าย ขอใบตรวจประวัติอาชญากรรม ขนาดเท่าไหร่",
      "อายุเอกสาร ใบตรวจประวัติอาชญากรรม",
      "ใบประวัติอาชญากรรม หมดอายุภายในกี่เดือน",
      "thai police certificate validity period",
      "แปลใบตรวจประวัติอาชญากรรมด้วยตนเอง",
      "ตัวอย่างใบตรวจประวัติอาชญากรรม ภาษาอังกฤษ",
      "ตัวอย่างหนังสือรับรองความประพฤติ",
      "thai police clearance certificate sample",
      "จองคิวออนไลน์ กองทะเบียนประวัติอาชญากร",
    ],
  },
  {
    id: "deep",
    label: "Long-tail / Concerns / Misspellings",
    keywords: [
      "เคยมีคดีความ ขอใบตรวจประวัติอาชญากรรมได้ไหม", "ลบประวัติอาชญากรรม ทำอย่างไร",
      "ติดแบล็กลิสต์ ขอหนังสือรับรองความประพฤติได้ไหม",
      "คดีสิ้นสุดแล้ว ประวัติอาชญากรรมยังอยู่ไหม",
      "ล้างมลทิน ประวัติอาชญากรรม",
      "ตรวจประวัติอาชญากรรม เจอคดีเก่า ยื่นวีซ่าผ่านไหม",
      "background check showing expunged record thailand",
      "how to clear criminal record in thailand",
      "thai police check with prior conviction", "will a misdemeanor affect my thai visa",
      "ใบตรวจประวัติอาชญากรรม แผ่นสีอะไร",
      "ตรวจประวัติอาชญากรรม ไม่ยอมไปพิมพ์ลายนิ้วมือ",
      "ขอใบตรวจประวัติอาชญากรรม แทนเพื่อน", "มอบอำนาจให้เอเจนซี่ ขอใบความประพฤติ",
      "ใบตรวจสอบประวัติอัชญากรรม", "ใบตรวจประวัติอาชญากรรม",
      "โปลิศ เคลียแรนซ์", "พลีส เช็ค ไทยแลนด์", "ใบเซอ ตรวจประวัติ",
      "ตรวจประวัติ ทะเบียนราษฎร์ กับ อาชญากรรม ต่างกันอย่างไร",
      "ขอใบตำรวจ ยื่นวีซ่าคู่หมั้น อเมริกา นานไหม",
      "แนะนำ เอเจนซี่ ขอใบรับรองความประพฤติ",
      "รีวิว ขอใบตรวจประวัติอาชญากรรม pantip",
      "ขอใบตรวจประวัติอาชญากรรม pantip",
      "ตรวจสอบใบตรวจประวัติอาชญากรรม ของแท้",
      "verify thai police clearance certificate",
      "ตรวจประวัติอาชญากรรม ย้อนหลังกี่ปี",
      "how many years back does thai background check go",
      "ขอหนังสือรับรองความประพฤติ สตช รอนานไหม",
      "ติดคดีเมาแล้วขับ ขอใบความประพฤติยื่นวีซ่าได้ไหม",
      "driving under the influence background check thailand",
      "ขอใบตำรวจ สำหรับวีซ่าถาวร",
      "แนะนำบริษัท รับทำ background check ในไทย",
      "จ้างคนเดินเรื่อง กองทะเบียนประวัติอาชญากร",
      "พิมพ์ลายนิ้วมือ แผ่น inkless thailand",
      "ขอใบตรวจประวัติอาชญากรรม โดยไม่ต้องเดินทางมาไทย",
      "apply for thai police check from overseas without coming back",
      "นายหน้า ขอหนังสือรับรองความประพฤติ ด่วน",
      "ตรวจสอบประวัติพนักงาน ก่อนเข้าทำงาน",
      "pre-employment background check thailand",
      "บริการตรวจสอบประวัติลูกจ้าง ต่างด้าว",
      "ตรวจประวัติอาชญากรรม แรงงานต่างด้าว 4 สัญชาติ",
      "police check for migrant workers thailand",
      "ขอใบตำรวจเพื่อขอสัญชาติอื่น",
      "police clearance for citizenship application",
      "ใบประวัติอาชญากรรม ต้องติดอากรแสตมป์ไหม",
      "ติดต่อ ศูนย์บริการหนังสือรับรองความประพฤติ เบอร์โทร",
      "police clearance certificate bangkok phone number",
      "เปิดบริการ วันไหนบ้าง ศูนย์หนังสือรับรองความประพฤติ",
      "police clearance bangkok opening hours",
      "ขอใบตรวจประวัติอาชญากรรม เสาร์ อาทิตย์ ได้ไหม",
      "ตรวจประวัติอาชญากรรม สำนักงานพิสูจน์หลักฐาน",
      "forensic science office background check thailand",
      "พิมพ์ลายนิ้วมือด้วยระบบดิจิตอล สำนักงานตำรวจ",
      "live scan fingerprinting bangkok",
      "ขอใบตำรวจไทย ไปทำงานออสเตรเลีย", "ขอใบตำรวจไทย ไปทำงานแคนาดา",
      "ขอใบตำรวจไทย ไปทำงานนิวซีแลนด์",
      "police clearance for new zealand visa thailand",
      "เอกสาร ยื่นขอประวัติอาชญากรรม ต่างชาติ ตัวจริง",
      "ขอประวัติอาชญากรรม ในนามบริษัท",
      "corporate background screening services thailand",
      "ตรวจสอบประวัติ กรรมการบริษัท",
      "background check for company directors thailand",
      "ใบตรวจประวัติอาชญากรรม หาย ทำอย่างไร",
      "ขอใบรับรองความประพฤติ ชุดใหม่ คัดสำเนา",
      "lost police clearance certificate thailand replacement",
      "ใบตรวจประวัติอาชญากรรม ต้องใช้พาสปอร์ตเล่มเก่าไหม",
      "เปลี่ยนพาสปอร์ตใหม่ ขอใบตรวจประวัติอาชญากรรม ต่างชาติ",
      "ขอใบตำรวจ แฟนฝรั่ง", "วีซ่าติดตามสามี ขอใบประวัติอาชญากรรม",
      "ขอหนังสือรับรองความประพฤติเพื่อไปอยู่กินฉันสามีภรรยาต่างประเทศ",
      "ตรวจประวัติอาชญากรรม เพื่อทำใบอนุญาตขับขี่สาธารณะ",
      "police check for public driving license thailand",
      "ขอใบความประพฤติ เพื่อประกอบอาชีพรักษาความปลอดภัย", "ตรวจประวัติ รปภ",
      "security guard background check thailand",
      "ตรวจประวัติอาชญากรรม สมัครขับ grab",
      "ขับรถส่งอาหาร ต้องตรวจประวัติอาชญากรรมไหม",
      "ตรวจประวัติอาชญากรรม คดีแพ่ง ขึ้นไหม",
      "does civil lawsuit show on thai criminal record check",
      "คดีล้มละลาย ขึ้นในใบตรวจประวัติอาชญากรรมไหม",
      "เช็คประวัติอาชญากรรม จากชื่อ นามสกุล",
      "background check thailand by name and dob",
      "พิมพ์ลายนิ้วมือส่งไปนอกประเทศ", "outbound fingerprinting services thailand",
      "ส่งใบพิมพ์ลายนิ้วมือไป fbi อเมริกา", "fbi fingerprinting service in bangkok",
      "ขอ rcpc ออสเตรเลีย ในไทย",
      "ขอใบตรวจประวัติอาชญากรรมด่วน แนะนำหน่อย",
      "ศูนย์รับแปลและเดินเรื่องประวัติอาชญากรรม สตช",
      "จ้างสืบประวัติอาชญากรรม", "ตรวจประวัติอาชญากรรม บุคคลอันตราย",
      "ขอใบความประพฤติทางไปรษณีย์ สำหรับคนอยู่ต่างจังหวัด",
      "ตรวจประวัติอาชญากรรม ด่วนที่สุด ราคาเท่าไหร่",
      "แนะนำเอเจนซี่ทำใบตำรวจด่วน pantip",
      "thai police check for immigration canada pr",
      "บริการรับทำประวัติอาชญากรรมครบวงจร แปล รับรอง ยื่นวีซ่า",
    ],
  },
] as const;

// ── PCC Service variants ─────────────────────────────────────────────────
export interface PccService {
  slug: string;
  nameTh: string;
  nameEn: string;
  category: "standard" | "express" | "foreigner" | "remote" | "bundle" | "corporate" | "specialty";
  priceTHB: number;
  turnaroundDays: string; // "5-7", "3-5", "1", "15-20"
  includes: string[];
  audience: string[];
  acceptedBy: string[];
}

export const PCC_SERVICES: PccService[] = [
  { slug: "standard", nameTh: "ขอใบตรวจประวัติอาชญากรรม แบบมาตรฐาน", nameEn: "Standard Police Clearance Certificate", category: "standard", priceTHB: 3500, turnaroundDays: "10-15", includes: ["จองคิวออนไลน์", "พาไปพิมพ์ลายนิ้วมือ", "รับเอกสารส่งถึงบ้าน"], audience: ["คนไทยยื่นวีซ่า", "PR", "ทำงานต่างประเทศ"], acceptedBy: ["USCIS", "UKVI", "DHA", "IRCC", "ICA", "New Zealand INZ"] },
  { slug: "express", nameTh: "ขอใบตรวจประวัติอาชญากรรม ด่วน 5-7 วัน", nameEn: "Express Police Clearance (5-7 days)", category: "express", priceTHB: 5500, turnaroundDays: "5-7", includes: ["Priority queue", "หน้างานเดินเรื่องเช้า-เย็น", "แจ้งเลขคิว realtime"], audience: ["ยื่นวีซ่ามีเส้นตาย", "สัมภาษณ์งานด่วน"], acceptedBy: ["USCIS", "UKVI", "DHA", "IRCC"] },
  { slug: "same-day", nameTh: "ขอใบตรวจประวัติอาชญากรรม ด่วนที่สุด (Same-Day Escort)", nameEn: "Same-Day Escort Police Check", category: "express", priceTHB: 8500, turnaroundDays: "1", includes: ["Escort ที่ปทุมวัน", "เดินเรื่องหน้าเคาน์เตอร์", "ยืนรอรับเอกสารเสร็จภายในวัน"], audience: ["Emergency embassy interview", "flight เพียง 48 ชม."], acceptedBy: ["USCIS", "DHA", "UKVI"] },
  { slug: "foreigner", nameTh: "PCC สำหรับชาวต่างชาติในไทย", nameEn: "Police Clearance for Foreigners in Thailand", category: "foreigner", priceTHB: 4500, turnaroundDays: "10-15", includes: ["ล่ามอังกฤษที่ปทุมวัน", "แปลข้อมูลกรอกฟอร์มไทย", "ประสาน work permit / non-B / LTR / SMART"], audience: ["Expats", "digital nomads", "teachers", "LTR/SMART/Elite visa holders"], acceptedBy: ["Home-country immigration", "Overseas employers", "Embassies"] },
  { slug: "from-abroad", nameTh: "ขอ PCC จากต่างประเทศ (ไม่ต้องบินกลับไทย)", nameEn: "Overseas Application via Power of Attorney", category: "remote", priceTHB: 6500, turnaroundDays: "15-25", includes: ["POA template ไทย-อังกฤษ", "รับ fingerprint จากตำรวจท้องถิ่นในประเทศคุณ", "เดินเรื่องแทนที่ปทุมวัน", "ส่ง EMS/DHL กลับ"], audience: ["คนไทยในต่างประเทศ", "อดีต expat ที่ออกจากไทยแล้ว"], acceptedBy: ["USCIS", "UKVI", "DHA", "IRCC", "INZ"] },
  { slug: "mail-delivery", nameTh: "รับเอกสารส่งถึงบ้าน ทั่วประเทศ", nameEn: "Nationwide Mail Delivery", category: "standard", priceTHB: 3800, turnaroundDays: "12-18", includes: ["EMS หรือ Kerry", "Tracking number", "รับประกันเอกสารเสียหาย"], audience: ["ต่างจังหวัด", "ไม่สะดวกเดินทางเข้ากทม."], acceptedBy: ["ทุกสถานทูตที่ปลายทาง"] },
  { slug: "with-translation", nameTh: "PCC + แปลรับรอง (NAATI / กงสุล / สาบาน)", nameEn: "PCC + Certified Translation Bundle", category: "bundle", priceTHB: 5500, turnaroundDays: "12-18", includes: ["PCC", "แปลอังกฤษ NAATI-certified หรือกงสุล", "รับรองลายเซ็นนักแปล"], audience: ["วีซ่าออสเตรเลีย/นิวซีแลนด์/แคนาดา", "AHPRA/ACS/CPA"], acceptedBy: ["DHA Australia", "INZ", "IRCC", "AHPRA"] },
  { slug: "with-mfa", nameTh: "PCC + รับรองกระทรวงการต่างประเทศ (MFA)", nameEn: "PCC + MFA Legalization", category: "bundle", priceTHB: 6000, turnaroundDays: "14-20", includes: ["PCC", "แปลอังกฤษ", "รับรอง MFA (กรมการกงสุล ถ.แจ้งวัฒนะ)"], audience: ["ประเทศที่ต้องการ chain of authentication"], acceptedBy: ["Chinese Embassy", "Japanese consulate", "Korean consulate", "UAE MoFA"] },
  { slug: "with-embassy", nameTh: "PCC + MFA + Legalization สถานทูตปลายทาง", nameEn: "Full Legalization Chain (PCC + MFA + Embassy)", category: "bundle", priceTHB: 8500, turnaroundDays: "18-25", includes: ["PCC", "แปล", "MFA", "Embassy stamp"], audience: ["ประเทศที่ไม่ยอมรับ MFA อย่างเดียว"], acceptedBy: ["China Embassy", "Taiwan TECO", "UAE Consulate", "Saudi Consulate"] },
  { slug: "fingerprint-mobile", nameTh: "พิมพ์ลายนิ้วมือนอกสถานที่ (Mobile Fingerprinting)", nameEn: "Mobile Fingerprinting Service", category: "specialty", priceTHB: 4500, turnaroundDays: "1", includes: ["Inkless digital scan", "หรือ ink 10-print card", "ทีมไปหาถึงโรงแรม/บ้าน"], audience: ["FBI check ในไทย", "RCMP Canada", "AFP Australia"], acceptedBy: ["FBI (US)", "RCMP", "AFP", "ACRO UK"] },
  { slug: "corporate", nameTh: "Corporate Bulk PCC (5-500 คน)", nameEn: "Corporate Bulk Screening", category: "corporate", priceTHB: 3200, turnaroundDays: "15-25", includes: ["HR account manager", "PDPA-compliant handling", "Bulk quote & invoice"], audience: ["HR", "Migration agent", "Recruiter"], acceptedBy: ["Foreign HR compliance", "Overseas employers"] },
  { slug: "migrant-worker", nameTh: "PCC สำหรับแรงงานต่างด้าว 4 สัญชาติ", nameEn: "Migrant Worker Police Clearance", category: "specialty", priceTHB: 4200, turnaroundDays: "12-18", includes: ["ล่ามพม่า/กัมพูชา/ลาว/เวียดนาม", "MOU compliance", "ประสานนายจ้าง"], audience: ["Migrant workers (MOU)", "Foreign construction crew"], acceptedBy: ["DOE", "MOL", "Home country embassies"] },
  { slug: "reissue", nameTh: "คัดสำเนา / ออกใหม่ ใบ PCC หาย", nameEn: "Lost PCC Reissue", category: "specialty", priceTHB: 3800, turnaroundDays: "10-15", includes: ["ค้นเลขทะเบียนคำร้องเก่า", "ยื่นคำร้องออกใหม่"], audience: ["เอกสารเดิมหาย", "ต้องการชุดสำเนาเพิ่ม"], acceptedBy: ["Same as original"] },
  { slug: "full-service", nameTh: "PCC ครบวงจร (Escort + แปล + MFA + Embassy + Visa filing)", nameEn: "End-to-End PCC Concierge", category: "bundle", priceTHB: 12500, turnaroundDays: "20-28", includes: ["ทุกขั้นตอนจบในสัญญาเดียว", "ประกัน timeline", "Dedicated case manager"], audience: ["VIP", "Corporate exec relocation", "Family migration"], acceptedBy: ["Any embassy chain worldwide"] },
];

export const PCC_SERVICE_BY_SLUG: Record<string, PccService> = Object.fromEntries(
  PCC_SERVICES.map((s) => [s.slug, s]),
);

// ── Service locations ────────────────────────────────────────────────────
export interface PccCity {
  slug: string;
  nameTh: string;
  nameEn: string;
  region: "Bangkok" | "Central" | "North" | "South" | "Northeast" | "East";
  bts?: string;
  mobileSurcharge?: number;
}

export const PCC_CITIES: PccCity[] = [
  { slug: "pathumwan", nameTh: "ปทุมวัน (กองทะเบียนฯ สตช)", nameEn: "Pathum Wan (RTP HQ)", region: "Bangkok", bts: "National Stadium / Siam BTS", mobileSurcharge: 0 },
  { slug: "sukhumvit", nameTh: "สุขุมวิท", nameEn: "Sukhumvit", region: "Bangkok", bts: "Asok / Nana / Phrom Phong", mobileSurcharge: 500 },
  { slug: "silom", nameTh: "สีลม", nameEn: "Silom", region: "Bangkok", bts: "Sala Daeng", mobileSurcharge: 500 },
  { slug: "sathorn", nameTh: "สาทร", nameEn: "Sathorn", region: "Bangkok", bts: "Chong Nonsi", mobileSurcharge: 500 },
  { slug: "asok", nameTh: "อโศก", nameEn: "Asok", region: "Bangkok", bts: "Asok BTS", mobileSurcharge: 500 },
  { slug: "ladprao", nameTh: "ลาดพร้าว", nameEn: "Ladprao", region: "Bangkok", bts: "Ladprao MRT", mobileSurcharge: 700 },
  { slug: "ratchada", nameTh: "รัชดา", nameEn: "Ratchada", region: "Bangkok", bts: "Rama IX MRT", mobileSurcharge: 700 },
  { slug: "thong-lo", nameTh: "ทองหล่อ", nameEn: "Thong Lo", region: "Bangkok", bts: "Thong Lo BTS", mobileSurcharge: 500 },
  { slug: "ekkamai", nameTh: "เอกมัย", nameEn: "Ekkamai", region: "Bangkok", bts: "Ekkamai BTS", mobileSurcharge: 500 },
  { slug: "phayathai", nameTh: "พญาไท", nameEn: "Phayathai", region: "Bangkok", bts: "Phaya Thai BTS/ARL", mobileSurcharge: 500 },
  { slug: "nonthaburi", nameTh: "นนทบุรี", nameEn: "Nonthaburi", region: "Central", mobileSurcharge: 1000 },
  { slug: "pathum-thani", nameTh: "ปทุมธานี", nameEn: "Pathum Thani", region: "Central", mobileSurcharge: 1200 },
  { slug: "samut-prakan", nameTh: "สมุทรปราการ", nameEn: "Samut Prakan", region: "Central", mobileSurcharge: 1000 },
  { slug: "chiang-mai", nameTh: "เชียงใหม่", nameEn: "Chiang Mai", region: "North", mobileSurcharge: 3000 },
  { slug: "phuket", nameTh: "ภูเก็ต", nameEn: "Phuket", region: "South", mobileSurcharge: 3500 },
  { slug: "pattaya", nameTh: "พัทยา", nameEn: "Pattaya", region: "East", mobileSurcharge: 1500 },
  { slug: "chonburi", nameTh: "ชลบุรี", nameEn: "Chonburi", region: "East", mobileSurcharge: 1500 },
  { slug: "samui", nameTh: "เกาะสมุย", nameEn: "Koh Samui", region: "South", mobileSurcharge: 4000 },
  { slug: "hua-hin", nameTh: "หัวหิน", nameEn: "Hua Hin", region: "Central", mobileSurcharge: 2000 },
  { slug: "korat", nameTh: "นครราชสีมา (โคราช)", nameEn: "Nakhon Ratchasima", region: "Northeast", mobileSurcharge: 2500 },
  { slug: "udon-thani", nameTh: "อุดรธานี", nameEn: "Udon Thani", region: "Northeast", mobileSurcharge: 3000 },
  { slug: "khon-kaen", nameTh: "ขอนแก่น", nameEn: "Khon Kaen", region: "Northeast", mobileSurcharge: 2800 },
];

export const PCC_CITY_BY_SLUG: Record<string, PccCity> = Object.fromEntries(
  PCC_CITIES.map((c) => [c.slug, c]),
);

// ── Destination jurisdictions ────────────────────────────────────────────
export const PCC_DESTINATIONS = [
  { slug: "usa", nameTh: "สหรัฐอเมริกา", nameEn: "United States", note: "แยกรายการวีซ่าที่สถานทูตจากคำขอ USCIS และตรวจเอกสารคดีเพิ่มเติมตามผู้รับ" },
  { slug: "uk", nameTh: "สหราชอาณาจักร", nameEn: "United Kingdom", note: "ตรวจสายงานและประเภทคำขอ UKVI ก่อนขอ PCC; งานวีซ่ากับการตรวจ DBS เป็นคนละเรื่อง" },
  { slug: "australia", nameTh: "ออสเตรเลีย", nameEn: "Australia", note: "ตรวจคำขอ Home Affairs; PCC ไทยไม่ใช่ Australian police check และเงื่อนไขผู้แปลขึ้นกับสถานที่แปล" },
  { slug: "canada", nameTh: "แคนาดา", nameEn: "Canada", note: "ตรวจคำแนะนำ IRCC ที่แยกตามสัญชาติ และเกณฑ์เอกสารของ Express Entry เมื่อใช้กระบวนการนั้น" },
  { slug: "new-zealand", nameTh: "นิวซีแลนด์", nameEn: "New Zealand", note: "ตรวจประเภทวีซ่า อายุผู้ยื่น และช่วงพำนักตาม INZ; ไม่ใช้เกณฑ์เดียวกับทุกคำขอ" },
  { slug: "schengen", nameTh: "เชงเก้น (EU)", nameEn: "Schengen (EU)", note: "ระบุประเทศผู้รับและแยกวีซ่าระยะสั้นจากการพำนักระยะยาว; ไม่มีชุด PCC เดียวสำหรับทุกประเทศ" },
  { slug: "japan", nameTh: "ญี่ปุ่น", nameEn: "Japan", note: "ตรวจคำขอเฉพาะของผู้รับญี่ปุ่นก่อนเลือกภาษาและการรับรอง ไม่กำหนด PCC กับทุกวีซ่า" },
  { slug: "korea", nameTh: "เกาหลีใต้", nameEn: "South Korea", note: "ตรวจรหัสวีซ่าและสำนักงานผู้รับ; ไม่เหมารวม F-2 หรือ F-6 ว่าต้องใช้ PCC และการรับรองแบบเดียวกัน" },
  { slug: "singapore", nameTh: "สิงคโปร์", nameEn: "Singapore", note: "แยกคำขอ ICA งาน MOM และการตรวจของนายจ้าง; ไม่ระบุว่า EP DP หรือ LTVP ต้องขอ PCC ทุกกรณี" },
  { slug: "china", nameTh: "จีน", nameEn: "China", note: "แยกวีซ่า ใบอนุญาตทำงาน และคำขอนายจ้าง; ตรวจคำแปลและการรับรองกับหน่วยงานผู้รับจริง" },
] as const;
